import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicLead = () => {
  const { ownerId, slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/brands/${ownerId}/${slug}`)
      .then(({ data }) => setBrand(data))
      .catch(() => setLoadErr(true));
  }, [ownerId, slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/public/leads/${ownerId}/${slug}`, form);
      setDone(true);
    } catch (err) {
      alert("Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Page not found</h1>
          <p className="text-gray-500">The brand you're looking for doesn't exist or is no longer active.</p>
        </div>
      </div>
    );
  }

  if (!brand) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="text-gray-500">Loading…</div></div>;
  }

  const primary = brand.primary_color || "#7C3AED";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-8 py-10" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` }}>
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.name} className="h-12 mb-3" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center text-white font-bold text-xl mb-3">{brand.name.charAt(0)}</div>
          )}
          <h1 className="text-2xl font-bold text-white">{brand.name}</h1>
          {brand.audience_description && <p className="text-white/80 text-sm mt-1">{brand.audience_description}</p>}
        </div>

        <div className="p-8">
          {done ? (
            <div className="text-center py-6" data-testid="lead-thanks">
              <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-2xl" style={{ background: primary }}>✓</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Thank you!</h2>
              <p className="text-gray-600 text-sm">We received your details and someone from our team will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="public-lead-form">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Get in touch</h2>
              <input className="op-input" placeholder="Your name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" />
              <input className="op-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" />
              <input className="op-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" />
              <input className="op-input" placeholder="Company (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <textarea className="op-input" rows={3} placeholder="What are you interested in?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              {/* Honeypot — hidden from real users, bots usually fill it */}
              <input
                type="text" name="website" tabIndex={-1} autoComplete="off"
                value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                style={{ position: "absolute", left: "-9999px", opacity: 0 }}
                aria-hidden="true"
              />
              <button type="submit" disabled={sending || !form.name.trim()}
                      className="w-full py-2.5 rounded-lg text-white font-semibold disabled:opacity-50"
                      style={{ background: primary }}
                      data-testid="lead-submit">
                {sending ? "Sending…" : "Submit"}
              </button>
              <p className="text-xs text-gray-400 text-center mt-2">We respect your privacy. Your information stays private.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicLead;
